import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { imageApi, ImageMetadata, extractionTriggeredCases } from '../../services/imageApi';
import { ImageGallery } from '../images/ImageGallery';
import { ImageViewer } from '../images/ImageViewer';
import { ImagingOverview } from '../images/ImagingOverview';
import { Image, AlertCircle } from 'lucide-react';
import { AnimatedLogo } from '../ask/AnimatedLogo';

// Module-level set to track cases where extraction has already completed or been checked
const extractionCompletedCases = new Set<string>();

interface ImagesTabProps {
  caseId: string;
}

export const ImagesTab: React.FC<ImagesTabProps> = ({ caseId }) => {
  const [selectedImage, setSelectedImage] = useState<ImageMetadata | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [checkingExtraction, setCheckingExtraction] = useState(
    !extractionCompletedCases.has(caseId)
  );

  const { data: images, isLoading, error, refetch } = useQuery(
    ['case-images', caseId],
    () => imageApi.getCaseImages(caseId),
    { staleTime: 30000 }
  );

  // Only trigger extraction if the initial image query comes back empty
  useEffect(() => {
    if (extractionCompletedCases.has(caseId) || isLoading) return;

    if (images && images.length > 0) {
      extractionCompletedCases.add(caseId);
      setCheckingExtraction(false);
      return;
    }

    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;

    const checkAndExtractImages = async () => {
      try {
        const status = await imageApi.checkExtractionStatus(caseId);

        if (!status.extractionNeeded) {
          extractionCompletedCases.add(caseId);
          if (!cancelled) setCheckingExtraction(false);
          return;
        }

        if (cancelled) return;
        setIsExtracting(true);

        if (!extractionTriggeredCases.has(caseId)) {
          extractionTriggeredCases.add(caseId);
          await imageApi.triggerImageExtraction(caseId);
        }

        if (cancelled) return;

        let attempts = 0;
        const maxAttempts = 15;

        pollInterval = setInterval(async () => {
          attempts++;
          try {
            const count = await imageApi.getExtractedImageCount(caseId);
            if (count > 0 || attempts >= maxAttempts) {
              if (pollInterval) clearInterval(pollInterval);
              extractionCompletedCases.add(caseId);
              if (!cancelled) {
                setIsExtracting(false);
                refetch();
              }
            }
          } catch (err: any) {
            if (err?.response?.status !== 404) {
              console.error('Error polling extraction status:', err);
            }
            if (attempts >= maxAttempts) {
              if (pollInterval) clearInterval(pollInterval);
              extractionCompletedCases.add(caseId);
              if (!cancelled) {
                setIsExtracting(false);
                refetch();
              }
            }
          }
        }, 2000);

      } catch (err: any) {
        if (err?.response?.status !== 404) {
          console.error('Error checking extraction status:', err);
        }
        if (!cancelled) {
          setIsExtracting(false);
          setCheckingExtraction(false);
        }
      }
    };

    checkAndExtractImages();

    return () => {
      cancelled = true;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [caseId, refetch, isLoading, images]);

  const filteredImages = images?.filter((image) =>
    !searchTerm || image.filename.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const emptyImages = !images || images.length === 0;
  if (isLoading || (isExtracting && emptyImages) || (checkingExtraction && emptyImages && !isLoading)) {
    return (
      <div className="p-6 h-full overflow-auto">
        <div className="bg-white dark:bg-medical-800 border border-gray-200 dark:border-medical-700 rounded-lg p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="mb-8">
              <AnimatedLogo size={96} />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-medical-100 mb-2">
              {isExtracting ? 'Extracting Images from Documents' : 'Loading Images'}
            </h3>
            <p className="text-gray-600 dark:text-medical-400 mb-4 max-w-md">
              {isExtracting
                ? 'Scanning PDFs and extracting medical images, charts, and diagrams...'
                : 'Retrieving image gallery for this case...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 h-full overflow-auto">
        <div className="bg-white dark:bg-medical-800 border border-gray-200 dark:border-medical-700 rounded-lg p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-medical-100 mb-2">Failed to load images</h3>
            <p className="text-gray-600 dark:text-medical-400 max-w-md">
              There was an error loading the image gallery. Please try again later.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 h-full overflow-auto bg-gray-50 dark:bg-medical-900">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-medical-100 mb-2">Medical Images</h2>
        <p className="text-gray-600 dark:text-medical-400">
          Images, charts, and diagrams extracted from case documents.
        </p>
      </div>

      {/* Imaging Overview */}
      <ImagingOverview caseId={caseId} extractedImageCount={images?.length || 0} />

      {/* Search */}
      {images && images.length > 0 && (
        <div className="mb-6 bg-white dark:bg-medical-800 border border-gray-200 dark:border-medical-700 rounded-lg p-4">
          <div className="flex items-center gap-4">
            <input
              type="text"
              placeholder="Search images..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field flex-1 max-w-md"
            />
            <div className="text-sm text-gray-600 dark:text-medical-400 ml-auto">
              {filteredImages.length} {filteredImages.length === 1 ? 'image' : 'images'}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {!images || images.length === 0 ? (
        <div className="bg-white dark:bg-medical-800 border border-gray-200 dark:border-medical-700 rounded-lg p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <Image className="w-16 h-16 text-gray-300 dark:text-medical-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-medical-100 mb-2">No images found</h3>
            <p className="text-gray-600 dark:text-medical-400 max-w-md">
              The uploaded evidence did not contain any extractable images.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-medical-800 border border-gray-200 dark:border-medical-700 rounded-lg overflow-hidden">
          <div className="flex h-[calc(100vh-280px)]">
            {/* Gallery */}
            <div className={selectedImage ? 'w-80 border-r dark:border-medical-700 overflow-y-auto' : 'flex-1 overflow-y-auto'}>
              <ImageGallery
                images={filteredImages}
                selectedImage={selectedImage}
                onSelectImage={setSelectedImage}
              />
            </div>

            {/* Viewer */}
            {selectedImage && (
              <div className="flex-1 overflow-hidden">
                <ImageViewer
                  image={selectedImage}
                  caseId={caseId}
                  onClose={() => setSelectedImage(null)}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
